import { ReactElement, ReactNode } from 'react';

import {
    Box,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import Icon from '@apigrowfinance/pbolt-icon';
import { CustomTheme, useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { useNavigate } from 'react-router-dom';

export type TimelineItemItemProps = {
    avatar?: ReactElement;
    primaryTitle?: ReactNode;
    primarySubtitle?: ReactNode;
    secondaryTitle?: ReactNode;
    secondarySubtitle?: ReactNode;
    testId?: string;
    highlightOnHover?: boolean;
    urlToNavigateTo?: string;
    panelMode?: boolean;
};

const TimelineItemTemplate = ({
    avatar,
    primaryTitle,
    primarySubtitle,
    secondaryTitle,
    secondarySubtitle,
    testId = '',
    highlightOnHover = false,
    urlToNavigateTo = '',
    panelMode = false,
}: TimelineItemItemProps) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    return (
        <Box data-testid={testId}>
            <ListItemButton
                disableGutters
                disableTouchRipple={!highlightOnHover}
                sx={{
                    py: 0,
                    width: '100%',
                    display: 'block',
                    // if not highlightable then disable the highlight effect
                    ...(!highlightOnHover && {
                        '&.MuiButtonBase-root:hover': {
                            bgcolor: 'transparent',
                            ...(panelMode
                                ? { cursor: 'cursor' }
                                : { cursor: 'default' }),
                        },
                    }),
                }}
                onClick={() => {
                    if (urlToNavigateTo != '') {
                        navigate(urlToNavigateTo);
                    }
                }}>
                <ListItem
                    disableGutters={panelMode}
                    sx={{ b: theme.spacing(1) }}
                    secondaryAction={
                        <ListItemText
                            primaryTypographyProps={{
                                style: {
                                    textAlign:
                                        theme.direction === 'rtl'
                                            ? 'left'
                                            : 'right',
                                },
                            }}
                            primary={
                                <Box data-testid="secondary-title">
                                    {secondaryTitle}
                                </Box>
                            }
                            secondaryTypographyProps={{
                                style: {
                                    textAlign:
                                        theme.direction === 'rtl'
                                            ? 'left'
                                            : 'right',
                                },
                            }}
                            secondary={secondarySubtitle}
                        />
                    }>
                    <ListItemAvatar>
                        {!!avatar && (
                            <Icon data-testid="avatar-icon">{avatar}</Icon>
                        )}
                    </ListItemAvatar>

                    <ListItemText
                        primary={
                            <Typography
                                variant="h6"
                                data-testid="primary-title"
                                sx={(theme: CustomTheme) => ({
                                    color:
                                        theme?.custom?.brand?.textMain ||
                                        'primary.main',
                                })}>
                                {primaryTitle}
                            </Typography>
                        }
                        secondary={primarySubtitle}
                    />
                </ListItem>
            </ListItemButton>
        </Box>
    );
};

export default TimelineItemTemplate;
